import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "./AuthContext";

/**
 * Global "current customer" context. A null id means "All customers" — the
 * cross-customer aggregate view. Customer-centric pages (M365, AI Actions, …)
 * read `currentCustomerId` to scope their queries; aggregate pages ignore it.
 * The selection persists in localStorage so it survives navigation/refresh.
 *
 * The customer list comes from GET /api/v1/customers, which is already
 * demo-mode + per-customer-access filtered server-side, so the switcher only
 * ever shows customers this user is allowed to see.
 */
export type CustomerLite = { id: number; name: string };

type CustomerContextValue = {
  currentCustomerId: number | null;
  current: CustomerLite | null;
  customers: CustomerLite[];
  setCurrentCustomer: (id: number | null) => void;
  refresh: () => void;
  loading: boolean;
};

const CustomerContext = createContext<CustomerContextValue | null>(null);
const STORAGE_KEY = "current_customer_id";

function initialId(): number | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

export function CustomerProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentCustomerId, setId] = useState<number | null>(initialId);

  const setCurrentCustomer = useCallback((id: number | null) => {
    setId(id);
    try {
      if (id == null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      /* localStorage unavailable — non-fatal */
    }
  }, []);

  const refresh = useCallback(() => {
    if (!user) return;
    setLoading(true);
    api
      .get("/api/v1/customers")
      .then((r) => {
        const list: CustomerLite[] = (r.data || []).map((c: { id: number; name: string }) => ({
          id: c.id,
          name: c.name,
        }));
        setCustomers(list);
      })
      .catch(() => setCustomers([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // If the persisted selection isn't in the accessible list (access changed,
  // customer deleted, demo toggled), fall back to "All customers".
  useEffect(() => {
    if (
      currentCustomerId != null &&
      customers.length > 0 &&
      !customers.some((c) => c.id === currentCustomerId)
    ) {
      setCurrentCustomer(null);
    }
  }, [customers, currentCustomerId, setCurrentCustomer]);

  const current = customers.find((c) => c.id === currentCustomerId) || null;

  return (
    <CustomerContext.Provider
      value={{ currentCustomerId, current, customers, setCurrentCustomer, refresh, loading }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer(): CustomerContextValue {
  const ctx = useContext(CustomerContext);
  if (!ctx) {
    return {
      currentCustomerId: null,
      current: null,
      customers: [],
      setCurrentCustomer: () => {},
      refresh: () => {},
      loading: false,
    };
  }
  return ctx;
}
